import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import PlayerForm from "@/components/PlayerForm";
import { api, resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Users, AlertTriangle, Timer, CheckCircle2, Clock, UserX } from "lucide-react";

const STATUS_META = {
  claimed: { label: "Active", cls: "bg-[#34C759] text-black", Icon: CheckCircle2 },
  invited: { label: "Invited", cls: "bg-[#007AFF] text-white", Icon: Clock },
  none: { label: "No login", cls: "bg-[#27272A] text-[#A1A1AA]", Icon: UserX },
};

function PlayerCard({ player, index, due, status, onClick }) {
  const meta = STATUS_META[status] || STATUS_META.none;
  const StatusIcon = meta.Icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      onClick={onClick}
      data-testid={`player-card-${player.jersey_number}`}
      className="group relative cursor-pointer bg-[#141414] border border-[#27272A] hover:border-white/20 hover:-translate-y-1 transition-[transform,border-color] duration-200 overflow-hidden"
    >
      <div className="relative h-56 overflow-hidden bg-[#1A1A1A]">
        {player.photo_url ? (
          <img
            src={resolveImg(player.photo_url)}
            alt={player.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#3f3f46]">
            <Users className="w-16 h-16" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-transparent" />
        <span className="absolute top-1 right-3 font-display text-7xl font-black text-white/15 leading-none select-none">
          {player.jersey_number}
        </span>
        <span className="absolute bottom-3 left-4 text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 bg-[#15803D] text-white">
          {player.position}
        </span>
        <span data-testid={`account-status-${player.jersey_number}`} className={`absolute bottom-3 right-3 flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-1 ${meta.cls}`}>
          <StatusIcon className="w-3 h-3" /> {meta.label}
        </span>
        {due && (
          <span data-testid={`retest-badge-${player.jersey_number}`} className="absolute top-3 left-3 flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-1 bg-[#FF9500] text-black">
            <Timer className="w-3 h-3" /> Re-test due
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-display text-2xl font-black uppercase tracking-tight leading-none truncate">
          {player.name}
        </h3>
        <div className="flex items-center gap-3 mt-2 text-xs text-[#71717A] font-mono-metric">
          <span>#{player.jersey_number}</span>
          {player.age && <span>{player.age} yrs</span>}
          {player.nationality && <span className="truncate">{player.nationality}</span>}
          <span>{player.preferred_foot?.[0]}</span>
        </div>
      </div>
    </motion.div>
  );
}

export default function Roster() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [due, setDue] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/players").then((res) => setPlayers(res.data)).finally(() => setLoading(false));
    api.get("/reminders/due-tests").then((res) => setDue(res.data.due)).catch(() => setDue([]));
    api.get("/players/account-status").then((res) => setStatusMap(res.data)).catch(() => setStatusMap({}));
  };
  useEffect(load, []);

  const dueIds = new Set(due.map((d) => d.player_id));

  const filtered = players.filter(
    (p) =>
      p.name.toLowerCase().includes(q.toLowerCase()) ||
      p.position.toLowerCase().includes(q.toLowerCase()) ||
      String(p.jersey_number).includes(q)
  );

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#71717A] mb-1">Squad</p>
          <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-none">
            The Roster
          </h1>
          <p className="text-[#A1A1AA] mt-2">{players.length} players registered</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
            <Input
              data-testid="roster-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search players"
              className="pl-9 w-full sm:w-56 bg-[#0A0A0A] border-[#27272A] rounded-none focus:border-[#15803D] focus:ring-1 focus:ring-[#15803D]"
            />
          </div>
          <Button
            data-testid="add-player-button"
            onClick={() => setShowForm(true)}
            className="rounded-none bg-[#15803D] hover:bg-[#166534] font-bold uppercase tracking-wider whitespace-nowrap transition-colors"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add Player
          </Button>
        </div>
      </div>

      {due.length > 0 && (
        <div data-testid="retest-reminder-banner" className="flex flex-col sm:flex-row sm:items-center gap-3 border border-[#FF9500]/40 bg-[#FF9500]/10 px-5 py-4 mb-6">
          <div className="flex items-center gap-3 shrink-0">
            <AlertTriangle className="w-5 h-5 text-[#FF9500]" />
            <span className="font-display text-lg font-black uppercase tracking-tight text-[#FF9500]">
              {due.length} {due.length === 1 ? "player" : "players"} due for a combine re-test
            </span>
          </div>
          <p className="text-sm text-[#A1A1AA]">
            No training test in the last 3 months: {due.map((d) => `${d.name} (#${d.jersey_number})`).join(", ")}
          </p>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-80 bg-[#141414] border border-[#27272A] animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-[#27272A]">
          <Users className="w-12 h-12 mx-auto text-[#3f3f46] mb-4" />
          <p className="text-[#A1A1AA] font-display text-2xl uppercase font-bold">No players yet</p>
          <p className="text-[#71717A] text-sm mt-1">Add your first player to build the squad.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((p, i) => (
            <PlayerCard key={p.id} player={p} index={i} due={dueIds.has(p.id)} status={statusMap[p.id]} onClick={() => navigate(`/players/${p.id}`)} />
          ))}
        </div>
      )}

      <PlayerForm open={showForm} onOpenChange={setShowForm} player={null} onSaved={load} />
    </Layout>
  );
}
